import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Checkbox, Flex } from 'antd';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  DeleteFormTableRowButton,
  FormDateRangePicker,
  FormField,
  TanstackFormTable,
  getFormTableFieldName,
} from '../../common/form';
import { formatDate } from '../../common/utils';
import { ExecutionPublicationFormDataType } from '../schema';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { getDatePickerMode, RequiredText } from '../../common/components';
import { useCalendarSelect } from './useCalendarSelect';
import { CalendarSelectModal } from './CalendarSelectModal';
import translations from '../translations/ExecutionPublications.translations.json';
import { useShowPastYear } from './useShowPastYear';
import { Frequency } from '../types';

const { addTranslations, useTranslation } = i18nNsFactory(
  'execution-publications-edit'
);
addTranslations(translations);

const columnHelper = createColumnHelper<ExecutionPublicationFormDataType>();

export function ExecutionPublicationsEditTable({
  frequency,
}: {
  frequency?: Frequency;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  const { cloneLastYear, selected, setSelected, updateValues } =
    useCalendarSelect('execution.executionPublications');
  const {
    showPastYear,
    onChange: onChangeShowPastYear,
    hideOldRows,
  } = useShowPastYear();

  const showModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleOk = useCallback(() => {
    setIsModalOpen(false);
    updateValues(selected);
  }, [updateValues, selected]);

  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor((rowData) => formatDate(rowData.executionDate), {
        id: 'executionDate',
        header: t('column.executionDate.label'),
        meta: { width: 120 },
        cell: ExecutionDateCell,
        filterFn: 'formattedDate',
        enableColumnFilter: false,
      }),
      columnHelper.accessor('validPeriod', {
        header: () => (
          <RequiredText>{t('column.validPeriod.label')}</RequiredText>
        ),
        enableColumnFilter: false,
        enableSorting: false,
        cell: (props) => <ValidPeriodCell {...props} frequency={frequency} />,
      }),
      columnHelper.accessor('operations' as any, {
        header: undefined,
        enableSorting: false,
        enableColumnFilter: false,
        cell: (props) => (
          <Flex justify="center" gap="small">
            <DeleteFormTableRowButton context={props} />
          </Flex>
        ),
        meta: {
          width: 40,
          className: 'sticky-right',
        },
      }),
    ],
    [frequency, t]
  );

  return (
    <CalendarTableWrapper>
      <Flex vertical gap={12}>
        <Flex justify="space-between" gap="small" align="center">
          <Checkbox
            id="show-past-year"
            checked={showPastYear}
            onChange={onChangeShowPastYear}
          >
            {t('checkbox.show-past-year')}
          </Checkbox>
          <Flex gap="small">
            <Button id="clone-year" onClick={cloneLastYear}>
              {t('button.clone-year')}
            </Button>
            <Button id="manage-dates" type="primary" onClick={showModal}>
              {t('button.manage-dates')}
            </Button>
          </Flex>
        </Flex>
        <TanstackFormTable
          name="execution.executionPublications"
          columns={columns}
          sticky
          scrollContainerSelector="#detail-layout-main-content"
          defaultSort={[{ id: 'executionDate', desc: true }]}
          rowHtmlAttributes={hideOldRows}
        />
      </Flex>
      <CalendarSelectModal
        title={t('modal.calendar.title')}
        isOpen={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        selected={selected}
        onSelect={setSelected}
      />
    </CalendarTableWrapper>
  );
}

const ExecutionDateCell: FC<
  CellContext<ExecutionPublicationFormDataType, any>
> = memo((props) => (
  <FormField
    name={getFormTableFieldName(props)}
    size="small"
    preview={{
      previewOnly: true,
      render: (value) => formatDate(value),
    }}
  />
));

const ValidPeriodCell: FC<
  CellContext<ExecutionPublicationFormDataType, any> & {
    frequency?: Frequency;
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);
  return (
    <FormField name={name} size="small" style={{ maxWidth: '300px' }}>
      <FormDateRangePicker
        name={name}
        size="small"
        autoFocus
        allowEmpty={[false, false]}
        picker={getDatePickerMode(props.frequency)}
        endOfPeriod
      />
    </FormField>
  );
});

const CalendarTableWrapper = styled.div`
  width: 100%;
`;
